import React, { useContext, useEffect, useState } from 'react';
import { changePassword, createNewPerson, getPersonById, getPersonLight, searchPersons, updatePerson } from 'app/api/clientApi';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import DateTextInput from 'app/component/date-text-input';
import * as Yup from 'yup';
import { ToastContext } from 'app/context/ToastContext';
import { Checkbox } from 'primereact/checkbox';
import ClientAdditionalInsuranceDialog from 'app/component/client-additional_insurance-dialog';

interface PersonProps {
  id?: any;
  value?: number;
  onPersonChanged: any;
  readonly?: boolean;
  showAccountFields?: boolean;
  pripoistenie?: boolean;
  insurances?: [];
  setInsurances?: any;
  additionalInsuranceOptions?: any[];
  onDelete?: any;
}

const validationSchema = Yup.object({
  firstName: Yup.string().max(255, 'Max 255 znakov').required('Meno je povinné'),
  lastName: Yup.string().max(255, 'Max 255 znakov').required('Priezvisko je povinné'),
  nrSupis: Yup.string().max(255, 'Max 255 znakov').required('Toto je povinný údaj'),
  postalCode: Yup.string().max(255, 'Max 255 znakov').required('Toto je povinný údaj'),
  city: Yup.string().max(255, 'Max 255 znakov').required('Toto je povinný údaj'),
  phone: Yup.string().required('Toto je povinný údaj').max(255, 'Max 255 znakov'),
  email: Yup.string().required('Toto je povinný údaj').max(255, 'Max 255 znakov'),
  birthNr: Yup.string().required('Toto je povinný údaj'),
});

export function getClientDataForm(
  createPerson,
  handleCreatePerson: (values: any) => void,
  savingLoading: boolean,
  setVisibleCreateDialog: (value: ((prevState: boolean) => boolean) | boolean) => void,
  showAccountFields?: boolean,
  readOnly?: boolean,
) {
  const { showSuccess, showError } = useContext(ToastContext);

  const sendEmail = email => {
    const mailtoLink = `mailto:${encodeURIComponent(email)}`;

    window.location.href = mailtoLink;
  };

  const onPhoneCall = phoneNr => {
    const telLink = `tel:${encodeURIComponent(phoneNr)}`;

    window.location.href = telLink;
  };

  const onChangePassword = userAccount => {
    changePassword(userAccount)
      .then(response => {
        showSuccess('Úspech', 'Heslo a/alebo emailová adresa boli úspešne zmenené.');
      })
      .catch(error => {
        console.error(error);
        showError('Chyba', error.response?.data.detail);
      });
  };

  function showAccountFieldsSection(setFieldValue, values, errors) {
    return (
      <>
        {values.userAccount?.id ? (
          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="p-field">
                <label htmlFor="userAccount.email">E-mailova adresa účtu</label>
                <Field name="userAccount.email" as={InputText} />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-field">
                <label htmlFor="userAccount.password">Heslo</label>
                <Field readOnly={readOnly} type="password" name="userAccount.password" as={InputText} />
                <ErrorMessage name="userAccount.password" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-4" style={{ marginTop: '20px' }}>
              <div className="p-field">
                <Button
                  disabled={readOnly}
                  type="button"
                  label="Zmeniť heslo / E-mail"
                  className="p-mt-3"
                  style={{ backgroundColor: '#e31818' }}
                  onClick={() => onChangePassword(values.userAccount)}
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid">
              <div className="col-12">
                <div className="p-field">
                  <label htmlFor="userAccount.createAccount">Vytvoriť účet</label>
                  <Checkbox
                    name="userAccount.createAccount"
                    disabled={readOnly}
                    onChange={e => void setFieldValue('userAccount.createAccount', e.checked)}
                    checked={values.userAccount?.createAccount}
                  ></Checkbox>
                </div>
              </div>
              {values.userAccount?.createAccount && (
                <>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="userAccount.email">E-mailova adresa účtu</label>
                      <Field readOnly={readOnly} name="userAccount.email" as={InputText} />
                      <ErrorMessage name="userAccount.email" component="div" className="p-error" />
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="userAccount.password">Heslo</label>
                      <Field readOnly={readOnly} type="password" name="userAccount.password" as={InputText} />
                      <ErrorMessage name="userAccount.password" component="div" className="p-error" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </>
    );

    return false;
  }

  return (
    <Formik
      initialValues={
        createPerson
          ? createPerson
          : {
              firstName: '',
              lastName: '',
              businessName: '',
              ico: '',
              dic: '',
              street: '',
              nrOrient: '',
              nrSupis: '',
              postalCode: '',
              city: '',
              phone: '',
              email: '',
              birthNr: '',
              dateOfBirth: '',
              titlePrefix: '',
              titlePostfix: '',
              corespStreet: '',
              corespNrOrient: '',
              corespNrSupis: '',
              corespPostalCode: '',
              corespCity: '',
            }
      }
      validationSchema={validationSchema}
      onSubmit={values => {
        handleCreatePerson(values);
      }}
    >
      {({ isSubmitting, setFieldValue, values, errors }) => {
        return (
          <>
            <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
              <div className="grid">
                <Divider align="left">
                  <div className="inline-flex align-items-center">
                    <b>Všeobecné informácie</b>
                  </div>
                </Divider>
                <div className="col-12 md:col-2">
                  <div className="p-field">
                    <label htmlFor="titlePrefix">Titul pred</label>
                    <Field readOnly={readOnly} name="titlePrefix" as={InputText} />
                    <ErrorMessage name="titlePrefix" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-4">
                  <div className="p-field">
                    <label htmlFor="firstName">Prvé meno*</label>
                    <Field readOnly={readOnly} name="firstName" as={InputText} />
                    <ErrorMessage name="firstName" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-4">
                  <div className="p-field">
                    <label htmlFor="lastName">Priezvisko*</label>
                    <Field readOnly={readOnly} name="lastName" as={InputText} />
                    <ErrorMessage name="lastName" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-2">
                  <div className="p-field">
                    <label htmlFor="titlePostfix">Titul za</label>
                    <Field readOnly={readOnly} name="titlePostfix" as={InputText} />
                    <ErrorMessage name="titlePostfix" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="dateOfBirth">Dátum narodenia</label>
                    <DateTextInput
                      id="dateOfBirth"
                      readOnly={readOnly}
                      value={values.dateOfBirth}
                      onChange={val => void setFieldValue('dateOfBirth', val)}
                    />
                    <ErrorMessage name="dateOfBirth" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="birthNr">Rodné číslo*</label>
                    <Field readOnly={readOnly} name="birthNr" as={InputText} />
                    <ErrorMessage name="birthNr" component="div" className="p-error" />
                  </div>
                </div>

                <Divider align="left">
                  <div className="inline-flex align-items-center">
                    <b>Informácie o firme</b>
                  </div>
                </Divider>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="businessName">Obchodné meno</label>
                    <Field readOnly={readOnly} name="businessName" as={InputText} />
                    <ErrorMessage name="businessName" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="p-field">
                    <label htmlFor="ico">IČO</label>
                    <Field readOnly={readOnly} name="ico" as={InputText} />
                    <ErrorMessage name="ico" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="p-field">
                    <label htmlFor="dic">DIČ</label>
                    <Field readOnly={readOnly} name="dic" as={InputText} />
                    <ErrorMessage name="dic" component="div" className="p-error" />
                  </div>
                </div>

                <Divider align="left">
                  <div className="inline-flex align-items-center">
                    <b>Kontaktné údaje</b>
                  </div>
                </Divider>
                <div className="col-10 md:col-5">
                  <div className="p-field">
                    <label htmlFor="email">E-mail*</label>
                    <Field readOnly={readOnly} name="email" as={InputText} />
                    <ErrorMessage name="email" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-2 md:col-1">
                  <div className="p-field" style={{ marginTop: '20px' }}>
                    <Button
                      icon="pi pi-send"
                      style={{ backgroundColor: '#e31818' }}
                      type="button"
                      onClick={() => sendEmail(values.email)}
                    ></Button>
                  </div>
                </div>
                <div className="col-10 md:col-5">
                  <div className="p-field">
                    <label htmlFor="phone">Tel. číslo*</label>
                    <Field readOnly={readOnly} name="phone" as={InputText} />
                    <ErrorMessage name="phone" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-2 md:col-1">
                  <div className="p-field" style={{ marginTop: '20px' }}>
                    <Button
                      icon="pi pi-phone"
                      style={{ backgroundColor: '#e31818' }}
                      type="button"
                      onClick={() => onPhoneCall(values.phone)}
                    ></Button>
                  </div>
                </div>

                <Divider align="left">
                  <div className="inline-flex align-items-center">
                    <b>Adresa</b>
                  </div>
                </Divider>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="street">Ulica</label>
                    <Field readOnly={readOnly} name="street" as={InputText} />
                    <ErrorMessage name="street" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="p-field">
                    <label htmlFor="nrSupis">Súpisné. číslo*</label>
                    <Field readOnly={readOnly} name="nrSupis" as={InputText} />
                    <ErrorMessage name="nrSupis" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="p-field">
                    <label htmlFor="nrOrient">Orientačné číslo</label>
                    <Field readOnly={readOnly} name="nrOrient" as={InputText} />
                    <ErrorMessage name="nrOrient" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="city">Obec*</label>
                    <Field readOnly={readOnly} name="city" as={InputText} />
                    <ErrorMessage name="city" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="postalCode">PSČ*</label>
                    <Field readOnly={readOnly} name="postalCode" as={InputText} />
                    <ErrorMessage name="postalCode" component="div" className="p-error" />
                  </div>
                </div>

                <Divider align="left">
                  <div className="inline-flex align-items-center">
                    <b>Korešpondenčná Adresa</b>
                  </div>
                </Divider>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="corespStreet">Ulica</label>
                    <Field readOnly={readOnly} name="corespStreet" as={InputText} />
                    <ErrorMessage name="corespStreet" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="p-field">
                    <label htmlFor="corespNrSupis">Súpisné. číslo</label>
                    <Field readOnly={readOnly} name="corespNrSupis" as={InputText} />
                    <ErrorMessage name="corespNrSupis" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="p-field">
                    <label htmlFor="corespNrOrient">Orientačné číslo</label>
                    <Field readOnly={readOnly} name="corespNrOrient" as={InputText} />
                    <ErrorMessage name="corespNrOrient" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="corespCity">Obec</label>
                    <Field readOnly={readOnly} name="corespCity" as={InputText} />
                    <ErrorMessage name="corespCity" component="div" className="p-error" />
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div className="p-field">
                    <label htmlFor="corespPostalCode">PSČ</label>
                    <Field readOnly={readOnly} name="corespPostalCode" as={InputText} />
                    <ErrorMessage name="corespPostalCode" component="div" className="p-error" />
                  </div>
                </div>
              </div>

              {showAccountFields && showAccountFieldsSection(setFieldValue, values, errors)}

              <div className="flex">
                <Button
                  label="Späť"
                  type="button"
                  icon="pi pi-arrow-left"
                  className="p-button-secondary mr-2"
                  style={{ backgroundColor: 'gray' }}
                  onClick={() => setVisibleCreateDialog(false)}
                />
                <Button
                  type="submit"
                  label="Uložiť"
                  icon="pi pi-check"
                  style={{ backgroundColor: '#e31818' }}
                  disabled={readOnly || savingLoading}
                />
              </div>
            </Form>
          </>
        );
      }}
    </Formik>
  );
}

const ClientWidget = ({
  id,
  value,
  onPersonChanged,
  readonly,
  pripoistenie,
  insurances,
  setInsurances,
  additionalInsuranceOptions,
  onDelete,
}: PersonProps) => {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [persons, setPersons] = useState([]);
  const [createPerson, setCreatePerson] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savingLoading, setSavingLoading] = useState(false);

  const [filter, setFilter] = useState<string>('');
  const [visibleSearchDialog, setVisibleSearchDialog] = useState(false);
  const [visibleCreateDialog, setVisibleCreateDialog] = useState(false);
  const { showSuccess, showError } = useContext(ToastContext);

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      if (value) {
        getPersonLight(value).then(response => {
          // setPersons([{ label: buildLabel(response.data), value: response.data.id }]);
          setSelectedPerson(response.data);
        });
      }
    }
  }, [firstLoad]);

  const fetchData = async (pageNumber: number, filterValue: string = '') => {
    setLoading(true);
    try {
      let params = '';
      if (filterValue) {
        params = '?filter=' + filterValue;
      }

      const response = await searchPersons(params);

      setPersons(response.data.content); // Reset options on new filter
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter) {
      const delayDebounceFn = setTimeout(() => {
        fetchData(0, filter);
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [filter]);

  const dialogFooter = (
    <div>
      <Button
        style={{ backgroundColor: 'gray' }}
        className="p-button-secondary"
        label="Zavrieť"
        icon="pi pi-times"
        onClick={() => setVisibleSearchDialog(false)}
      />
    </div>
  );

  const handleSelectPerson = (rowData: any) => {
    getPersonLight(rowData.id).then(response => {
      setSelectedPerson(response.data);
    });

    onPersonChanged(rowData.id);
    setVisibleSearchDialog(false); // Close the modal after selection
  };

  // Template for the "Select" button in each row
  const selectButtonTemplate = (rowData: any) => (
    <Button label="Vybrať" type="button" style={{ backgroundColor: '#e31818' }} onClick={() => handleSelectPerson(rowData)} />
  );

  const onOpenSearchModal = () => {
    setFilter('');
    setPersons([]);
    setVisibleSearchDialog(true);
  };

  const onOpenCreateModal = personId => {
    if (personId) {
      setLoading(true);
      getPersonById(personId)
        .then(response => {
          setCreatePerson(response.data);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      setVisibleSearchDialog(false);
    }

    setVisibleCreateDialog(true);
  };

  const handleCreatePerson = (values: any) => {
    setSavingLoading(true);

    let call;
    if (createPerson?.id) {
      call = updatePerson(createPerson.id, values);
    } else {
      call = createNewPerson(values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Osoba úspešne uložená.');
        setSelectedPerson(response.data);
        onPersonChanged(response.data.id);
        setVisibleCreateDialog(false);
      })
      .catch(error => {
        console.error('Error creating agent:', error);
        showError('Chyba', error.response?.data?.detail);
      })
      .finally(() => setSavingLoading(false));
  };

  return (
    <div className="p-inputgroup flex-1">
      <div
        style={{
          width: '100%',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '10px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left section with text info */}
          <div onClick={!readonly ? (selectedPerson ? () => onOpenCreateModal(selectedPerson?.id) : onOpenSearchModal) : null}>
            {selectedPerson?.businessName && (
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                {selectedPerson?.businessName} ({selectedPerson?.ico})
              </div>
            )}
            {!selectedPerson?.businessName && (
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                {selectedPerson?.firstName} {selectedPerson?.lastName}
              </div>
            )}
            <div style={{ margin: '5px 0' }}>{selectedPerson?.birthNr}</div>
            <div>{selectedPerson?.email}</div>
          </div>

          {/* Right section with button */}
          {!readonly && selectedPerson?.id && (
            <div style={{ display: 'flex' }}>
              <Divider layout="vertical" />
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Button
                  label="Nájsť"
                  type="button"
                  onClick={onOpenSearchModal}
                  size="small"
                  icon="pi pi-search"
                  style={{ backgroundColor: '#e31818', height: '30px', marginBottom: '5px' }}
                />
                <Button
                  label="Detail"
                  type="button"
                  onClick={() => onOpenCreateModal(selectedPerson?.id)}
                  size="small"
                  icon="pi pi-info-circle"
                  style={{ backgroundColor: '#e31818', height: '30px' }}
                />
                {onDelete && (
                  <Button
                    label="Odstrániť"
                    type="button"
                    onClick={onDelete}
                    size="small"
                    icon="pi pi-trash"
                    severity="danger"
                    style={{ backgroundColor: '#e31818', height: '30px', marginTop: '5px' }}
                  />
                )}
              </div>
            </div>
          )}
          {!readonly && !selectedPerson?.id && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                style={{ backgroundColor: '#e31818' }}
                label="Nájsť osobu"
                type="button"
                onClick={onOpenSearchModal}
                size="small"
                icon="pi pi-search"
              />
            </div>
          )}
        </div>
        {pripoistenie && (
          <ClientAdditionalInsuranceDialog
            clientId={value}
            insurances={insurances}
            setInsurances={setInsurances}
            additionalInsuranceOptions={additionalInsuranceOptions}
            readOnly={readonly}
          />
        )}
      </div>

      <Dialog
        header="Nájsť klienta"
        visible={visibleSearchDialog}
        style={{ width: '80vw' }}
        footer={dialogFooter}
        onHide={() => setVisibleSearchDialog(false)}
      >
        {/* InputText for user query */}
        <div className="p-field col-12" style={{ width: '100%' }}>
          <InputText
            style={{ width: '100%' }}
            id="query"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Hľadať osobu"
          />
        </div>

        {/* DataTable to show results */}
        {persons.length > 0 && (
          <DataTable stripedRows loading={loading} size="small" lazy value={persons} responsiveLayout="scroll" className="p-mt-4">
            <Column field="nameFullFormatted" header="Meno" />
            <Column field="birthNr" header="Rodné číslo" />
            <Column field="ico" header="IČO" />
            <Column field="email" header="E-mail" />
            <Column field="phone" header="Telefón" />
            <Column field="agentName" header="Agent" />
            <Column body={selectButtonTemplate} header="Akcia" />
          </DataTable>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
          <Button
            label="Vytvoriť novú osobu"
            type="button"
            id="vytvorit-osobut-button-id"
            style={{ backgroundColor: '#e31818', color: 'white' }}
            onClick={() => onOpenCreateModal(null)}
            size="large"
          />
        </div>
      </Dialog>

      <Dialog
        header="Detail o klientovi"
        visible={visibleCreateDialog}
        style={{ width: '80vw' }}
        onHide={() => setVisibleCreateDialog(false)}
      >
        {/* InputText for user query */}
        {loading && (
          <div className="flex justify-content-center">
            <ProgressSpinner />
          </div>
        )}

        {!loading && (
          <>
            <div>{getClientDataForm(createPerson, handleCreatePerson, savingLoading, setVisibleCreateDialog)}</div>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ClientWidget;
